/**
 * Modal for creating a new admin user. On success the modal becomes a
 * "post-create" panel showing the resolved Slack ID (if `users.lookupByEmail`
 * succeeded server-side) and a one-click onboarding-email generator.
 */
import { useState } from "react";

import {
  adminApi,
  type AdminUser,
  type OnboardingEmailDraft,
} from "../../api/admin";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/toast/context";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function UserAddModal({ open, onClose }: Props) {
  const rolesQuery = adminApi.roles.list();
  const createMutation = adminApi.users.create();
  const toast = useToast();

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [cronEnabled, setCronEnabled] = useState(false);
  const [roleNames, setRoleNames] = useState<string[]>([]);
  const [created, setCreated] = useState<AdminUser | null>(null);

  const reset = () => {
    setEmail("");
    setName("");
    setCronEnabled(false);
    setRoleNames([]);
    setCreated(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error("Email is required");
      return;
    }
    createMutation.mutate(
      {
        email: trimmed,
        name: name.trim() || undefined,
        role_names: roleNames.length ? roleNames : undefined,
        cron_enabled: cronEnabled,
      },
      {
        onSuccess: (user) => {
          setCreated(user);
          toast.ok("User created");
        },
        onError: (e) => toast.error(e),
      },
    );
  };

  const toggleRole = (name: string) => {
    setRoleNames((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );
  };

  const allRoles = rolesQuery.data ?? [];

  return (
    <>
      <Modal open={open} onClose={handleClose} title="Add user" eyebrow="Admin">
        {created ? (
          <CreatedView user={created} onClose={handleClose} />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label" htmlFor="user-add-email">
                Microsoft email
              </label>
              <input
                id="user-add-email"
                className="input"
                type="email"
                placeholder="user@fluidmobile.de"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="label" htmlFor="user-add-name">
                Display name
              </label>
              <input
                id="user-add-name"
                className="input"
                placeholder="(optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Roles</label>
              {allRoles.length === 0 ? (
                <span className="text-sm text-ink-low">
                  No roles defined yet — create some on the Roles tab first.
                </span>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {allRoles.map((role) => {
                    const checked = roleNames.includes(role.name);
                    return (
                      <label
                        key={role.id}
                        className="tag-chip"
                        style={{
                          cursor: "pointer",
                          borderColor: checked
                            ? "var(--c-aqua-medium-1)"
                            : undefined,
                          background: checked
                            ? "var(--c-aqua-soft)"
                            : undefined,
                          color: checked ? "var(--c-aqua-dark)" : undefined,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleRole(role.name)}
                          style={{ marginRight: 4 }}
                        />
                        {role.name}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={cronEnabled}
                  onChange={(e) => setCronEnabled(e.target.checked)}
                />
                <span className="text-sm">Enable scheduled (cron) jobs</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-rule">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-main"
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? "Creating…" : "Create user"}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}

function CreatedView({
  user,
  onClose,
}: {
  user: AdminUser;
  onClose: () => void;
}) {
  const toast = useToast();
  const onboardingMutation = adminApi.users.onboardingEmail(user.id);
  const [draft, setDraft] = useState<OnboardingEmailDraft | null>(null);

  const copy = (text: string, label: string) => {
    void navigator.clipboard.writeText(text).then(() => toast.ok(`${label} copied`));
  };

  return (
    <div className="space-y-4">
      <div className="panel" style={{ padding: "1rem" }}>
        <div className="text-xs font-heading font-semibold text-ink-low mb-1">
          Created
        </div>
        <div className="font-heading font-semibold text-ink">{user.email}</div>
        <div className="text-sm text-ink-low mt-1">
          Slack mapping:{" "}
          {user.slack_user_id ? (
            <span className="tag-chip" style={{ marginLeft: 4 }}>
              {user.slack_user_id}
            </span>
          ) : (
            <span className="text-yellow-dark">
              not found via users.lookupByEmail — set manually in user detail
            </span>
          )}
        </div>
        <div className="text-sm text-ink-low mt-2">
          Roles:{" "}
          {user.role_names.length === 0
            ? "—"
            : user.role_names.join(", ")}
        </div>
      </div>

      <div>
        <button
          type="button"
          className="btn btn-second btn-sm"
          disabled={onboardingMutation.isPending}
          onClick={() => {
            onboardingMutation.mutate(undefined, {
              onSuccess: (d) => {
                setDraft(d);
                toast.ok("Draft generated");
              },
              onError: (e) => toast.error(e),
            });
          }}
        >
          Generate onboarding email
        </button>
        {draft ? (
          <div className="mt-4 panel" style={{ padding: "1rem" }}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-heading font-semibold text-ink-low">
                Subject
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => copy(draft.subject, "Subject")}
              >
                Copy
              </button>
            </div>
            <div className="text-sm font-medium text-ink mb-3">
              {draft.subject}
            </div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-heading font-semibold text-ink-low">
                Body
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => copy(draft.body, "Body")}
              >
                Copy
              </button>
            </div>
            <pre className="args-json">{draft.body}</pre>
          </div>
        ) : null}
      </div>

      <div className="flex justify-end pt-2 border-t border-rule">
        <button type="button" className="btn btn-main" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
}
